import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Modal,
} from "react-native";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { observer, Observer } from "mobx-react";
import GlobalStyles from "../../../Resources/Styles";
import Colors from "../../../Resources/Color";
import Fonts from "../../../Resources/Fonts";
import { Octicons } from "@expo/vector-icons";
import { Entypo } from "@expo/vector-icons";
import { Menu, MenuItem } from "react-native-material-menu";
import { useScreenDimensions, useDevice } from "../../_layout";

import CustomAlert from "../../Components/CustomAlert";
import { useTranslation } from "react-i18next";

import IconEmptyView from "../../Components/IconEmptyView";
import AddBlockView from "../../Block/AddBlockView";
import DeleteBlockView from "../../Block/DeleteBlockView";
import ClientWeekStatusText from "../../Components/ClientWeekStatusText";
import SearchBar from "../../Components/SearchBar";
import CTAButton from "../../Components/CTAButton";
import CustomActivityIndicator from "../../Components/CustomActivityIndicator";
import BackButton from "../../Components/BackButton";
import WorkoutsDashboardTabBar from "../../Components/WorkoutsDashboardTabBar";
import BlockDashboard from "../../Block/BlockDashboard";
import BlockView from "../../Block/shared/BlockView";
import BlockCard from "../../Block/shared/BlockCard";
import { useDrag, useDrop } from "react-dnd";
import {
  commitBlockLibraryOrder,
  moveItem,
} from "./blockLibraryReorderUtils";

const COACH_LIBRARY_BLOCK_DND_TYPE = "COACH_LIBRARY_BLOCK";

const getDOMElement = (ref) => {
  if (!ref) return null;
  if (ref.getBoundingClientRect) return ref;
  if (ref._component) return ref._component;
  if (typeof ref.getNode === "function") return ref.getNode();
  return ref;
};

const hasSameOrder = (left, right) => {
  if (!Array.isArray(left) || !Array.isArray(right)) return false;
  if (left.length !== right.length) return false;
  return left.every((id, index) => id === right[index]);
};

const BlockLibrary = ({ coach }) => {
  const { t, i18n } = useTranslation();
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();

  const [showingAddNewBlock, setShowingAddNewBlock] = useState(false);
  const [pressSuppressedBlockId, setPressSuppressedBlockId] = useState(null);
  const pressSuppressTimeoutRef = useRef(null);
  const dragStartIdsRef = useRef([]);
  const draggedBlockIdRef = useRef(null);

  useEffect(
    () => () => {
      if (pressSuppressTimeoutRef.current) {
        clearTimeout(pressSuppressTimeoutRef.current);
        pressSuppressTimeoutRef.current = null;
      }
    },
    [],
  );

  const schedulePressSuppressionClear = useCallback(() => {
    if (pressSuppressTimeoutRef.current) {
      clearTimeout(pressSuppressTimeoutRef.current);
    }

    pressSuppressTimeoutRef.current = setTimeout(() => {
      setPressSuppressedBlockId(null);
      pressSuppressTimeoutRef.current = null;
    }, 220);
  }, []);

  const resetDragState = useCallback(() => {
    dragStartIdsRef.current = [];
    draggedBlockIdRef.current = null;
  }, []);

  const handleBlockDragStart = useCallback((blockId, orderedIds) => {
    if (!Array.isArray(orderedIds) || orderedIds.length < 2) return;

    if (pressSuppressTimeoutRef.current) {
      clearTimeout(pressSuppressTimeoutRef.current);
      pressSuppressTimeoutRef.current = null;
    }

    const normalizedBlockId = String(blockId);
    dragStartIdsRef.current = [...orderedIds];
    draggedBlockIdRef.current = normalizedBlockId;
  }, []);

  const handleBlockDrop = useCallback(
    ({ draggedId, dropIndex }) => {
      const startIds = dragStartIdsRef.current;
      if (!Array.isArray(startIds) || startIds.length === 0) return;

      const sourceIndex = startIds.findIndex((id) => id === draggedId);
      if (sourceIndex < 0) return;
      if (sourceIndex === dropIndex) return;

      const finalIds = moveItem(startIds, sourceIndex, dropIndex);
      if (hasSameOrder(startIds, finalIds)) return;

      commitBlockLibraryOrder({ coach, orderedIds: finalIds });
    },
    [coach],
  );

  const handleBlockDragEnd = useCallback(() => {
    const draggedBlockId = draggedBlockIdRef.current;

    resetDragState();
    if (draggedBlockId) {
      setPressSuppressedBlockId(draggedBlockId);
    }
    schedulePressSuppressionClear();
  }, [resetDragState, schedulePressSuppressionClear]);

  const CopyBlockToClient = ({ isVisible, onClose, block }) => {
    const [selectedClients, setSelectedClients] = useState([]);

    const [notBlocked, setNotBlocked] = useState([]);

    function toggleVariable() {
      setNotBlocked();

      setTimeout(() => {
        setNotBlocked(true);
        onClose();
      }, 2500); // 2000 milliseconds = 2 seconds
    }

    const SearchBar = () => {
      const [keyword, setKeyword] = useState("");
      const onChangeText = (text) => {
        setKeyword(text);
        block.coach.setSearchTerm(text);
      };

      const [isHovered, setIsHovered] = useState(false);

      return (
        <View
          style={{
            flexDirection: "row",
            width: "100%",
            justifyContent: "center",
            paddingHorizontal: 20,
            marginTop: 20,
            marginBottom: 10,
          }}
        >
          <View
            style={[
              styles.searchBar,
              {
                backgroundColor: "transparent",
                // width: "50%",
                flex: 1,
                borderWidth: 0.2,
                // borderColor: Colors.grayColor,

                borderColor: isHovered
                  ? "rgba(0, 0, 0, 0.5)"
                  : Colors.borderColor,
              },
            ]}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <Octicons name="search" size={20} color={Colors.grayColor} />
            <TextInput
              value={keyword}
              onChangeText={onChangeText}
              style={[
                styles.textInputStyle,
                { color: Colors.blackColor, outline: "none", width: "100%" },
              ]}
              placeholder={t("searchClient-string")}
              placeholderTextColor={Colors.grayColor}
              disabled={!notBlocked}
              maxLength={100}
            />
          </View>
          <Buttons bool={false} />
          <Buttons bool={true} />
        </View>
      );
    };

    const ClientRow = observer(({ client, index }) => {
      const isSelected = selectedClients.includes(client.id);
      const length = block.coach
        .getFilteredClients()
        .filter((client) => client.status === "active-string").length;
      const select = () => {
        if (isSelected) {
          // Append the client's userId to the array
          setSelectedClients((prevSelectedClients) =>
            prevSelectedClients.filter((id) => id !== client.id)
          );
        } else {
          // Remove the client's userId from the array
          setSelectedClients((prevSelectedClients) => [
            ...prevSelectedClients,
            client.id,
          ]);
        }
        block.coach.setSearchTerm("");
      };

      const [isHovered, setIsHovered] = useState(false);

      return (
        <View
          style={{
            flexDirection: "row",
            flex: 1,
            //marginTop: 20,
            padding: 5,
            paddingHorizontal: 16,

            alignItems: "center",
            // borderRadius: 12,
            backgroundColor: isSelected ? Colors.bubbleColor : "transparent",
            paddingVertical: 8,

            borderBottomLeftRadius: index === length - 1 ? 14 : 0,
            borderBottomRightRadius: index === length - 1 ? 14 : 0,

            opacity: isHovered ? 0.9 : 1,
          }}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <TouchableOpacity
            onPress={select}
            style={{ flex: 1 }}
            disabled={!notBlocked}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <View
                style={{
                  alignItems: "flex-start",
                  // marginHorizontal: 10,
                  width: "100%",
                  paddingLeft: isHovered ? 5 : 0,
                }}
              >
                <Text
                  style={{
                    color: isSelected ? Colors.whiteColor : Colors.blackColor,
                    fontSize: 16,
                    fontWeight: isHovered ? "600" : "normal",
                  }}
                >
                  {client.nameForCoach} {client.surnameForCoach}
                </Text>
                <ClientWeekStatusText
                  client={client}
                  style={{
                    color: isSelected
                      ? Colors.lightGrayColor
                      : Colors.grayColor,
                    fontSize: 12,
                    marginTop: 2,
                  }}
                  showColorForUnprepared={isSelected ? false : true}
                />
              </View>
            </View>
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
              }}
            />
            <View
              style={{
                //height: selectedClientName === client.name ? 2 : 1,
                backgroundColor: Colors.grayColor,
                // marginTop: 5,
              }}
            />
          </TouchableOpacity>
        </View>
      );
    });

    const Cancel = () => {
      block.coach.setSearchTerm("");
      onClose();
    };
    const Buttons = ({ bool }) => {
      const text = bool ? "save-string" : "cancel-string";
      const canAdd = selectedClients.length > 0;
      const Save = async () => {
        if (canAdd) {
          toggleVariable();

          try {
            const copyPromises = selectedClients.map(async (clientId) => {
              const clientObject = block.coach.clients.find(
                (cli) => cli.id === clientId
              );
              if (clientObject) {
                await clientObject.copyBlockToClient(t, block, true);
              } else {
                console.error(`Client not found with ID: ${clientId}`);
                // Handle the case where the client object wasn't found
              }
            });

            // Wait for all copy operations to complete
            await Promise.all(copyPromises);

            // All operations completed successfully, you can safely close the modal or update the UI here
            // onClose();
          } catch (error) {
            console.error("Error copying block to clients:", error);
            // Handle any errors that occurred during the copy operations
          }
        }
      };

      const [isHovered, setIsHovered] = useState(false);

      return bool ? (
        <TouchableOpacity
          onPress={Save}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          disabled={!notBlocked}
        >
          <View
            style={{
              // marginHorizontal: 20,
              backgroundColor: Colors.bubbleColor,
              borderRadius: 14,
              borderWidth: 1,
              justifyContent: "center",
              borderColor: Colors.bubbleColor,
              width: 80,
              height: 35,
              justifyContent: "center",
              alignItems: "center",
              opacity: isHovered ? 0.8 : canAdd ? 1 : 0.5,
            }}
          >
            {!notBlocked ? (
              <CustomActivityIndicator large={false} white={true} />
            ) : (
              <Text
                style={{
                  textAlignVertical: "center",
                  textAlign: "center",
                  color: Colors.whiteColor,
                }}
              >
                {t(text)}
              </Text>
            )}
          </View>
        </TouchableOpacity>
      ) : (
        <TouchableOpacity
          onPress={Cancel}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          disabled={!notBlocked}
        >
          <View
            style={{
              marginHorizontal: 10,
              backgroundColor: isHovered ? Colors.redColor : Colors.whiteColor,
              borderRadius: 14,
              borderWidth: 1,
              justifyContent: "center",
              borderColor: "red",
              width: 80,
              height: 35,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Text
              style={{
                textAlignVertical: "center",
                textAlign: "center",
                color: isHovered ? "white" : Colors.redColor,
              }}
            >
              {t(text)}
            </Text>
          </View>
        </TouchableOpacity>
      );
    };

    return (
      <Modal
        animationType="fade"
        transparent={true}
        visible={isVisible}
        onRequestClose={notBlocked && onClose}
      >
        <View style={GlobalStyles.centeredView}>
          <TouchableOpacity
            onPress={Cancel}
            style={GlobalStyles.touchableBackground}
            disabled={!notBlocked}
          >
            <View />
          </TouchableOpacity>
          <View
            style={{
              position: "absolute",
              // height: "100%",
              // width: "100%",
              alignItems: "center",
              justifyContent: "center",
            }}
            onStartShouldSetResponder={() => false}
          >
            <View
              style={[
                styles.modalView,
                {
                  maxWidth: screenSize === "small" ? 360 : 450,
                  width: "90%",
                  maxHeight: screenSize === "small" ? 450 : 600,
                  alignSelf: "center",
                  // height: "100%",
                },
              ]}
            >
              <SearchBar />
              <View
                style={{
                  marginTop: 10,
                  width: "100%",
                }}
              >
                <Text
                  style={{
                    color: Colors.grayColor,
                    fontWeight: "500",
                    marginLeft: 15,
                    marginBottom: 4,
                  }}
                >
                  {t("athletes-string")}
                </Text>
                <View
                  style={{
                    height: 0.5,

                    // backgroundColor: "rgba(255, 255, 255, 0.2)",
                    backgroundColor: Colors.grayColor,
                  }}
                />
              </View>
              <Observer>
                {() => (
                  <ScrollView
                    style={{
                      marginHorizontal: 0,
                      marginBottom: 0,
                      borderBottomLeftRadius: 14,
                      borderBottomRightRadius: 14,
                    }}
                    showsVerticalScrollIndicator={false}
                  >
                    {block.coach &&
                    block.coach
                      .getFilteredClients()
                      .filter((client) => client.status === "active-string")
                      .length > 0 ? (
                      <>
                        {block.coach
                          .getFilteredClients()
                          .filter((client) => client.status === "active-string")
                          .map((client, index) => (
                            <View key={client.id}>
                              <ClientRow client={client} index={index} />
                              {index <
                                block.coach
                                  .getFilteredClients()
                                  .filter(
                                    (client) =>
                                      client.status === "active-string"
                                  ).length -
                                  1 && (
                                <View
                                  style={{
                                    height: 0.5,
                                    // backgroundColor: "rgba(255, 255, 255, 0.2)",
                                    backgroundColor: Colors.grayColor,
                                    // marginHorizontal: 20,
                                  }}
                                />
                              )}
                            </View>
                          ))}
                      </>
                    ) : (
                      <>
                        <IconEmptyView
                          iconLibrary={"Ionicons"}
                          mainText={"emptyClientsTitle-string"}
                          subText={"noAthletesFoundText-string"}
                          iconName={"people-outline"}
                          marginTop={40}
                          marginBottom={8}
                        />
                      </>
                    )}
                  </ScrollView>
                )}
              </Observer>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  const BlockRow = observer(
    ({
      block,
      dragIndex,
      orderedIds,
      canReorder,
      suppressPress = false,
    }) => {
      const blockId = String(block?.id ?? "");

      const [{ isOver }, drop] = useDrop({
        accept: COACH_LIBRARY_BLOCK_DND_TYPE,
        canDrop: (item) => {
          if (!canReorder) return false;
          if (!item || item.type !== COACH_LIBRARY_BLOCK_DND_TYPE) return false;
          return item.id !== blockId;
        },
        drop: (item, monitor) => {
          if (!canReorder) return;
          if (!item || item.type !== COACH_LIBRARY_BLOCK_DND_TYPE) return;
          if (monitor.didDrop()) return;
          return { dropIndex: dragIndex };
        },
        collect: (monitor) => ({
          isOver: monitor.isOver({ shallow: true }),
        }),
      });

      const [{ isDragging }, drag] = useDrag({
        type: COACH_LIBRARY_BLOCK_DND_TYPE,
        canDrag: () => canReorder,
        item: () => {
          const startIndex = Array.isArray(orderedIds)
            ? orderedIds.findIndex((id) => id === blockId)
            : dragIndex;

          handleBlockDragStart(blockId, orderedIds);

          return {
            type: COACH_LIBRARY_BLOCK_DND_TYPE,
            id: blockId,
            index: startIndex >= 0 ? startIndex : dragIndex,
          };
        },
        end: (item, monitor) => {
          const dropResult = monitor.getDropResult();
          if (
            canReorder &&
            dropResult &&
            typeof dropResult.dropIndex === "number" &&
            item?.id
          ) {
            handleBlockDrop({ draggedId: item.id, dropIndex: dropResult.dropIndex });
          }
          handleBlockDragEnd();
        },
        collect: (monitor) => ({
          isDragging: monitor.isDragging(),
        }),
      });

      const dragDropRef = useCallback(
        (node) => {
          const domNode = getDOMElement(node);
          if (!domNode) {
            drag(null);
            drop(null);
            return;
          }
          drag(drop(domNode));
        },
        [drag, drop],
      );

    const [showingDeleteAlert, setShowingDeleteAlert] = useState(false);
    const deleteAlertTitle = "deleteWorkoutAlertTitle-string";
    const deleteAlertText = "deleteWorkoutAlertText-string";

    const [showingEditBlock, setShowingEditBlock] = useState(false);
    const [showingCopyToClient, setShowingCopyToClient] = useState(false);

    const action = () => {
      block.setProperty("isSelected", true);
    };

    const deleteLibraryBlock = () => {
      if (block.coach) {
        block.coach.updateCoachDeletionBlocks(block.id);
        block.coach.startOrResetBatchTimer();
      }

      coach.setProperty(
        "blockLibrary",
        (coach.blockLibrary = coach.blockLibrary.filter(
          (b) => b.id !== block.id
        ))
      );
    };

    const handleMenuAction = (menuBlock, itemKey) => {
      if (!menuBlock) return;
      if (itemKey === "edit") {
        setShowingEditBlock(true);
        return;
      }
      if (itemKey === "copyToClient") {
        setShowingCopyToClient(true);
        return;
      }
      if (itemKey === "delete") {
        setShowingDeleteAlert(true);
      }
    };

    const blockMenuItems = [
      {
        key: "edit",
        label: t("edit-string"),
        icon: { type: "ionicons", name: "create-outline" },
      },
      {
        key: "copyToClient",
        label: t("copyToAClient-string"),
        icon: { type: "ionicons", name: "person-add-outline" },
      },
      {
        key: "delete",
        label: t("delete-string"),
        icon: { type: "ionicons", name: "trash-outline" },
        isDanger: true,
      },
    ];

      return (
        <>
          <BlockCard
            block={block}
            folderKey="coach-library"
            blockId={block.id}
            screenSize={screenSize}
            i18n={i18n}
            t={t}
            menuItems={blockMenuItems}
            onPress={action}
            onSummaryPress={() => {}}
            onMenuAction={handleMenuAction}
            showRightIcons={true}
            showSummaryAction={false}
            showInitialDate={false}
            timelineVariant="neutral"
            dragNodeRef={dragDropRef}
            isDragging={isDragging}
            draggingOpacity={0}
            isDropZoneActive={isOver && !isDragging}
            suppressPress={suppressPress}
          />
          {showingCopyToClient && (
            <CopyBlockToClient
              isVisible={showingCopyToClient}
              onClose={() => setShowingCopyToClient(false)}
              block={block}
            />
          )}
          {showingDeleteAlert && (
            <CustomAlert
              isVisible={showingDeleteAlert}
              onClose={() => setShowingDeleteAlert(false)}
              title={deleteAlertTitle}
              text={deleteAlertText}
              action={deleteLibraryBlock}
              specialCTA={"delete-string"}
            />
          )}
          {showingEditBlock && (
            <AddBlockView
              isVisible={showingEditBlock}
              onClose={() => setShowingEditBlock(false)}
              block={block}
              coach={coach}
            />
          )}
        </>
      );
    },
  );

  const LibraryBlockDescription = observer(({ block }) => {
    const [showingDeleteProtocolAlert, setShowingDeleteProtocolAlert] =
      useState(false);

    const [showingCopyToClient, setShowingCopyToClient] = useState(false);

    const [showingDeleteAlert, setShowingDeleteAlert] = useState(false);
    const deleteAlertTitle = "deleteBlockAlertTitle-string";
    const deleteAlertText = "deleteBlockAlertText-string";

    const [blockMenu, setBlockMenu] = useState(false);
    const showBlockMenu = () => setBlockMenu(true);

    const handleLibraryTabSelect = useCallback(
      (key) => {
        block.setProperty("isShowingDashboard", key === "dashboard");
      },
      [block],
    );

    const BlockTitle = observer(() => {
      const [isHovered, setIsHovered] = useState(false);

      const MenuItemView = ({ index }) => {
        const [isHovered, setIsHovered] = useState(false);

        const showCopyToClient = () => {
          setShowingCopyToClient(true);
          setBlockMenu(false);
        };

        const showDeleteAlert = () => {
          setShowingDeleteAlert(true);
          setBlockMenu(false);
        };

        return index === 0 ? (
          <MenuItem
            onPress={showCopyToClient}
            style={{ minWidth: 190 }}
            textStyle={{
              fontSize: 14,
              fontWeight: isHovered ? "600" : "normal",
            }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {t("copyToAClient-string")}
          </MenuItem>
        ) : (
          <MenuItem
            onPress={showDeleteAlert}
            style={{ minWidth: 190 }}
            textStyle={{
              fontSize: 14,
              color: "red",
              fontWeight: isHovered ? "600" : "normal",
            }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {t("delete-string")}
          </MenuItem>
        );
      };

      return (
        <View
          style={{
            width: screenSize === "small" && "100%",
            ...(screenSize === "small" ? { flex: 1 } : {}),
            // borderWidth: 1,
            flexShrink: 1,
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Text
              style={[Fonts.bold18, { marginRight: 12 }]}
              numberOfLines={2}
              onPress={() => {
                if (screenSize === "small") {
                  if (block.coach.selectedBlock) {
                    block.coach.setSelectedBlock(null);
                  } else {
                    block.coach.setSelectedBlock(null);
                  }
                }
              }}
            >
              {block.name}
            </Text>

            <Menu
              visible={blockMenu}
              anchor={
                <TouchableOpacity
                  onPress={showBlockMenu}
                  style={{ opacity: isHovered ? 0.5 : 1 }}
                  onMouseEnter={() => !isMobile && setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                >
                  <Entypo
                    name="dots-three-horizontal"
                    size={18}
                    color="black"
                  />
                </TouchableOpacity>
              }
              style={[GlobalStyles.menu]}
              onRequestClose={() => setBlockMenu(false)}
            >
              <MenuItemView index={0} />
              <MenuItemView index={1} />
            </Menu>
          </View>

          <View
            style={{
              backgroundColor: Colors.blackColor,
              height: 1,
              opacity: 0.2,
              marginTop: 8,
            }}
          />
        </View>
      );
    });

    const deleteLibraryBlock = () => {
      coach.updateCoachDeletionBlocks(block.id);
      coach.startOrResetBatchTimer();
      coach.setProperty(
        "blockLibrary",
        (coach.blockLibrary = coach.blockLibrary.filter(
          (b) => b.id !== block.id
        ))
      );
      setShowingDeleteAlert(false);
    };

    return (
      <View
        style={{
          // flex: 1,
          justifyContent: "space-between",
        }}
      >
        <View
          style={{
            // flexDirection: screenSize === "large" ? "row" : "column",
            // gap: screenSize === "small" ? 8 : 24,
            width: "100%",
          }}
        >
          {screenSize != "small" && (
            <View style={{ marginBottom: 12 }}>
              <BackButton
                action={() => {
                  block.setProperty("isSelected", false);
                }}
                backString={t("back-string")}
              />
            </View>
          )}

          <View
            style={{
              width: "100%",
              flexDirection: screenSize !== "small" && "row",
              marginBottom: 16,
              alignItems: "center",
            }}
          >
            <BlockTitle block={block} />
            {screenSize != "small" && <View style={{ flex: 1 }} />}
            <View
              style={{
                width: screenSize === "small" && "100%",
                flexDirection: "row",
                marginTop: screenSize === "small" && 16,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <View
                style={[
                  GlobalStyles.HStack,
                  {
                    justifyContent: "space-evenly",
                    flexDirection: "row",
                    marginLeft: screenSize === "small" ? 12 : 16,
                  },
                ]}
              >
                <WorkoutsDashboardTabBar
                  selectedKey={block.isShowingDashboard ? "dashboard" : "workouts"}
                  onSelect={handleLibraryTabSelect}
                  workoutsLabel={t("workouts-string")}
                  dashboardLabel={t("dashboard-string")}
                  itemGap={12}
                />
              </View>
            </View>
          </View>

          {block.isShowingDashboard ? (
            <BlockDashboard block={block} />
          ) : (
            <BlockView
              block={block}
              showTutorialLink={false}
              showExerciseHistory={false}
            />
          )}
        </View>

        {showingDeleteAlert && (
          <DeleteBlockView
            isVisible={showingDeleteAlert}
            onClose={() => setShowingDeleteAlert(false)}
            title={deleteAlertTitle}
            text={deleteAlertText}
            action={deleteLibraryBlock}
            nameOfBlock={block ? block.name : ""}
          />
        )}
        {showingCopyToClient && (
          <CopyBlockToClient
            isVisible={showingCopyToClient}
            onClose={() => setShowingCopyToClient(false)}
            block={block}
          />
        )}
      </View>
    );
  });

  return (
    <View
      style={[
        styles.container,
        { marginTop: screenSize === "small" ? 16 : 24 },
      ]}
    >
      <Observer>
        {() => {
          const filteredBlocks = coach.getFilteredBlockLibrary();
          const orderedBlockIds = filteredBlocks.map((entry) =>
            String(entry?.id ?? ""),
          );
          const canReorderBlocks =
            !coach.selectedBlock &&
            (coach.searchTermBlockLibrary?.trim?.() ?? "") === "" &&
            filteredBlocks.length > 1;

          return (
            <>
            {coach.selectedBlock ? (
              <LibraryBlockDescription block={coach.selectedBlock} />
            ) : (
              <>
                <View
                  style={[
                    GlobalStyles.HStack,
                    { marginBottom: 16, maxWidth: "45%" },
                  ]}
                >
                  <SearchBar
                    text={coach.searchTermBlockLibrary}
                    onChangeText={(text) =>
                      coach.setProperty("searchTermBlockLibrary", text)
                    }
                    placeholder={t("searchBlock-string")}
                    autoFocusOnDesktop={true}
                  />

                  <CTAButton
                    plus={true}
                    text={t("newBlock-string")}
                    action={() => setShowingAddNewBlock(true)}
                    disabled={false}
                  />
                </View>

                {filteredBlocks.length === 0 ? (
                  <IconEmptyView
                    iconLibrary={"Ionicons"}
                    mainText={"emptyBlocksTitle-string"}
                    subText={"createTemplateBlocksText-string"}
                    iconName={"folder-outline"}
                    marginTop={130}
                  />
                ) : (
                  <View
                    style={{
                      flexDirection: "row",
                      justifyContent: "flex-start",
                      flexWrap: "wrap",
                      marginTop: screenSize === "small" ? 0 : 8,
                      gap: screenSize === "small" ? 8 : 12,
                    }}
                  >
                    {filteredBlocks.map((block, index) => (
                      <BlockRow
                        block={block}
                        key={block.id}
                        dragIndex={index}
                        orderedIds={orderedBlockIds}
                        canReorder={canReorderBlocks}
                        suppressPress={
                          pressSuppressedBlockId === String(block?.id ?? "")
                        }
                      />
                    ))}
                  </View>
                )}

                {showingAddNewBlock && (
                  <AddBlockView
                    isVisible={showingAddNewBlock}
                    onClose={() => setShowingAddNewBlock(false)}
                    coach={coach}
                  />
                )}
              </>
            )}
            </>
          );
        }}
      </Observer>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.backgroundColor,
  },
  title: {
    fontSize: 28,
    fontWeight: "500",
  },
  statisticLabel: {
    fontSize: 13,
  },
  statisticValue: {
    flex: 1,
    fontSize: 13,
    fontWeight: "500",
    marginLeft: 5,
  },
  textInputStyle: {
    flex: 1,
    paddingHorizontal: 10,
    borderRadius: 14,
    borderWidth: 0,
    fontWeight: "400",
    fontSize: 14,
  },
  searchBar: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    borderRadius: 14,
    height: 40,
  },
  flex1: {
    flex: 1,
  },
  modalView: {
    borderRadius: 18,
    backgroundColor: Colors.whiteColor,
  },
});

export default BlockLibrary;
